export type KpiRangePreset =
  | "thisMonth"
  | "lastMonth"
  | "thisQuarter"
  | "lastQuarter"
  | "yearToDate"
  | "lastYear"
  | "dateRange";

export interface KpiRangeOption {
  label: string;
  value: KpiRangePreset;
}

export const KPI_RANGE_OPTIONS: KpiRangeOption[] = [
  { label: "This Month", value: "thisMonth" },
  { label: "Last Month", value: "lastMonth" },
  { label: "This Quarter", value: "thisQuarter" },
  { label: "Last Quarter", value: "lastQuarter" },
  { label: "Year-To-Date", value: "yearToDate" },
  { label: "Last Year", value: "lastYear" },
  { label: "Date Range", value: "dateRange" },
];

/** What is saved in the dashboard config. fromDate / toDate are yyyy-MM-dd and only used by dateRange. */
export interface KpiRangeSetting {
  preset: KpiRangePreset;
  fromDate?: string | null;
  toDate?: string | null;
}

/** Inclusive calendar days, local time, both at midnight. */
export interface KpiDates {
  from: Date;
  to: Date;
}

export const DEFAULT_KPI_RANGE: KpiRangeSetting = { preset: "thisMonth" };

export class KpiRangeFns {
  /** Null when a Date Range is incomplete or runs backwards - the KPI widgets then ask for dates. */
  static resolve(setting: KpiRangeSetting | null | undefined, today = new Date()): KpiDates | null {
    const t = KpiRangeFns.day(today.getFullYear(), today.getMonth(), today.getDate());
    const y = t.getFullYear();
    const m = t.getMonth();
    const q = Math.floor(m / 3) * 3;

    switch (setting?.preset ?? DEFAULT_KPI_RANGE.preset) {
      case "thisMonth":
        return { from: KpiRangeFns.day(y, m, 1), to: t };
      case "lastMonth":
        return { from: KpiRangeFns.day(y, m - 1, 1), to: KpiRangeFns.day(y, m, 0) };
      case "thisQuarter":
        return { from: KpiRangeFns.day(y, q, 1), to: t };
      case "lastQuarter":
        return { from: KpiRangeFns.day(y, q - 3, 1), to: KpiRangeFns.day(y, q, 0) };
      case "yearToDate":
        return { from: KpiRangeFns.day(y, 0, 1), to: t };
      case "lastYear":
        return { from: KpiRangeFns.day(y - 1, 0, 1), to: KpiRangeFns.day(y - 1, 11, 31) };
      case "dateRange": {
        const from = KpiRangeFns.parseIsoDay(setting?.fromDate);
        const to = KpiRangeFns.parseIsoDay(setting?.toDate);
        if (!from || !to || from > to) return null;
        return { from, to };
      }
    }
  }

  static toIsoDay(d: Date): string {
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${d.getFullYear()}-${mm}-${dd}`;
  }

  static parseIsoDay(s: string | null | undefined): Date | null {
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s ?? "");
    if (!match) return null;
    return KpiRangeFns.day(+match[1], +match[2] - 1, +match[3]);
  }

  /** Month and day may overflow or go to zero; Date normalizes them (day 0 = last day of the previous month). */
  private static day(year: number, month: number, date: number): Date {
    return new Date(year, month, date);
  }
}
